<template>
    <div class="min-h-screen bg-base-200 p-4">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="flex justify-center items-center h-screen">
            <span class="loading loading-spinner loading-lg"></span>
        </div>

        <!-- 密码验证界面 -->
        <div v-else-if="requiresPassword && !isAuthenticated" class="flex justify-center items-center h-screen">
            <div class="card w-96 bg-base-100 shadow-xl">
                <div class="card-body">
                    <h2 class="card-title justify-center text-2xl mb-4">
                        🔒 {{ $tt('需要访问密码') }}
                    </h2>
                    <p class="text-center text-gray-600 mb-4">{{ $tt('此播放列表受密码保护') }}</p>
                    <div class="form-control">
                        <input
                            type="password"
                            v-model="password"
                            :placeholder="$tt('请输入访问密码')"
                            class="input input-bordered"
                            @keyup.enter="verifyPassword"
                        />
                    </div>
                    <div class="card-actions justify-center mt-4">
                        <button
                            class="btn btn-neutral btn-wide"
                            @click="verifyPassword"
                            :disabled="isVerifying || !password"
                        >
                            <span v-if="isVerifying" class="loading loading-spinner"></span>
                            {{ isVerifying ? $tt('验证中...') : $tt('确认') }}
                        </button>
                    </div>
                    <div v-if="errorMessage" class="alert alert-error mt-4">
                        <span>{{ errorMessage }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- 播放列表内容 -->
        <div v-else-if="playlistData" class="max-w-7xl mx-auto">
            <!-- 分享信息横幅 -->
            <div class="alert alert-info mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div>
                    <div class="font-bold">{{ $tt('此播放列表由 {user} 分享', { user: sharedBy }) }}</div>
                    <div class="text-sm">{{ $tt('分享时间：{time}', { time: formatDate(createdAt) }) }}</div>
                </div>
            </div>

            <!-- 播放列表信息 -->
            <div class="card bg-base-100 shadow-xl mb-4">
                <div class="card-body">
                    <div class="flex items-start gap-4">
                        <div v-if="playlistData.image" class="avatar">
                            <div class="w-32 h-32 rounded-lg">
                                <img :src="playlistData.image" :alt="playlistData.name" />
                            </div>
                        </div>
                        <div class="flex-1">
                            <h1 class="card-title text-3xl mb-2">{{ playlistData.name }}</h1>
                            <p v-if="playlistData.comment" class="text-gray-600 mb-2">
                                {{ playlistData.comment }}
                            </p>
                            <div class="text-sm text-gray-500">
                                {{ $tt('{count} 首歌曲', { count: playlistData.tracks.length }) }}
                            </div>
                        </div>
                    </div>

                    <!-- 操作按钮 -->
                    <div class="card-actions justify-end mt-4">
                        <button class="btn btn-neutral" @click="playAll">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                            {{ $tt('播放全部') }}
                        </button>
                        <button
                            v-if="canCopy"
                            class="btn btn-primary"
                            @click="copyPlaylist"
                            :disabled="isCopying"
                        >
                            <span v-if="isCopying" class="loading loading-spinner"></span>
                            {{ isCopying ? $tt('复制中...') : $tt('复制到我的播放列表') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- 曲目列表 -->
            <div class="card bg-base-100 shadow-xl">
                <div class="card-body">
                    <h2 class="card-title mb-4">{{ $tt('曲目列表') }}</h2>
                    <TrackList
                        v-if="playlistData.tracks.length > 0"
                        :tracks="playlistData.tracks"
                        :index-field="'index'"
                        :draggable="false"
                    />
                    <div v-else class="text-center py-8 text-gray-500">
                        {{ $tt('此播放列表暂无歌曲') }}
                    </div>
                </div>
            </div>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="flex justify-center items-center h-screen">
            <div class="card w-96 bg-base-100 shadow-xl">
                <div class="card-body">
                    <h2 class="card-title justify-center text-2xl mb-4 text-error">
                        ⚠️ {{ $tt('访问失败') }}
                    </h2>
                    <p class="text-center text-gray-600">{{ errorMessage }}</p>
                    <div class="card-actions justify-center mt-4">
                        <button class="btn btn-neutral" @click="$router.push('/')">
                            {{ $tt('返回首页') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TrackList from '@/views/library/track/TrackList'

export default {
    name: 'SharedPlaylist',
    components: {
        TrackList
    },
    data() {
        return {
            token: this.$route.params.shareCode,
            isLoading: true,
            requiresPassword: false,
            isAuthenticated: false,
            password: '',
            isVerifying: false,
            playlistData: null,
            permission: null,
            canCopy: false,
            canEdit: false,
            sharedBy: '',
            createdAt: '',
            errorMessage: '',
            isCopying: false
        }
    },
    async created() {
        await this.loadSharedPlaylist()
    },
    methods: {
        async loadSharedPlaylist() {
            this.isLoading = true
            this.errorMessage = ''

            try {
                const res = await this.$api.music.share.accessShared({ token: this.token })

                if (res.result) {
                    // 成功获取播放列表
                    this.playlistData = res.data.playlist
                    this.permission = res.data.permission
                    this.canCopy = res.data.can_copy
                    this.canEdit = res.data.can_edit
                    this.sharedBy = res.data.shared_by
                    this.createdAt = res.data.created_at
                    this.isAuthenticated = true
                } else if (res.requires_password) {
                    // 需要密码
                    this.requiresPassword = true
                } else {
                    // 其他错误
                    this.errorMessage = res.message || this.$tt('无法访问此分享')
                }
            } catch (error) {
                console.error('加载分享播放列表失败:', error)
                this.errorMessage = this.$tt('网络错误，请稍后重试')
            } finally {
                this.isLoading = false
            }
        },

        async verifyPassword() {
            if (!this.password) return

            this.isVerifying = true
            this.errorMessage = ''

            try {
                const res = await this.$api.music.share.accessShared({
                    token: this.token,
                    password: this.password
                })

                if (res.result) {
                    this.playlistData = res.data.playlist
                    this.permission = res.data.permission
                    this.canCopy = res.data.can_copy
                    this.canEdit = res.data.can_edit
                    this.sharedBy = res.data.shared_by
                    this.createdAt = res.data.created_at
                    this.isAuthenticated = true
                    this.requiresPassword = false
                } else {
                    this.errorMessage = res.message || this.$tt('密码错误')
                }
            } catch (error) {
                console.error('验证密码失败:', error)
                this.errorMessage = this.$tt('验证失败，请重试')
            } finally {
                this.isVerifying = false
            }
        },

        async copyPlaylist() {
            if (!this.canCopy) {
                this.$myMsg.notify({
                    title: this.$tt('无权限'),
                    content: this.$tt('此分享不允许复制'),
                    type: 'error',
                    time: 2000
                })
                return
            }

            this.isCopying = true

            try {
                const res = await this.$api.music.share.copyShared({ token: this.token })

                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('复制成功！'),
                        content: this.$tt('播放列表已复制到您的账户'),
                        type: 'success',
                        time: 2000
                    })
                    // 可以跳转到新的播放列表
                    if (res.data.new_playlist_id) {
                        setTimeout(() => {
                            this.$router.push(`/playlist/${res.data.new_playlist_id}`)
                        }, 1000)
                    }
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('复制失败'),
                        content: res.message || this.$tt('未知错误'),
                        type: 'error',
                        time: 2000
                    })
                }
            } catch (error) {
                console.error('复制播放列表失败:', error)
                this.$myMsg.notify({
                    title: this.$tt('复制失败'),
                    content: this.$tt('网络错误'),
                    type: 'error',
                    time: 2000
                })
            } finally {
                this.isCopying = false
            }
        },

        playAll() {
            if (this.playlistData && this.playlistData.tracks.length > 0) {
                this.$store.dispatch('playTrackList', {
                    tracks: this.playlistData.tracks
                })
            }
        },

        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            return date.toLocaleString('zh-CN')
        }
    }
}
</script>

<style scoped>
/* 可以添加自定义样式 */
</style>
